import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

// Brand Activation Item Component
const BrandActivationItem: React.FC<{
  image: string;
  index: number;
}> = ({ image, index }) => {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);

  return (
    <div 
      ref={cardRef}
      className={`group relative overflow-hidden rounded-xl transition-all duration-500 hover:shadow-2xl hover:shadow-brand-gold/20 hover:-translate-y-2 ${
        cardVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        animationDelay: `${index * 100}ms`,
        transitionDelay: `${index * 100}ms`
      }}
    >
      {/* Image Container */}
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img 
          src={image} 
          alt="Brand Activation Campaign"
          className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
        />

        {/* Brand Activation Floating Elements */}
        <div className="absolute top-4 right-4 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:animate-bounce">
          <span className="text-white text-sm">BA</span>
        </div>
        <div className="absolute top-4 left-4 w-6 h-6 bg-brand-gold/80 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700 group-hover:animate-pulse"></div>
      </div>
    </div>
  );
};

const BrandActivationPage: React.FC = () => {
  const [headerRef, headerVisible] = useScrollAnimation(0.3);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [statsRef, statsVisible] = useScrollAnimation(0.4);

  // Brand Activation images from the folder - All actual images from public/Brand Activation
  const brandActivationImages = [
    "/Brand Activation/041f855f-688e-4cce-ad2e-4bc027bbb6c5.jpg",
    "/Brand Activation/05a22b81-dcc6-490f-80f1-8a46dace31cb.jpg",
    "/Brand Activation/094168f3-92dc-484d-a6d1-d269668f3daf.jpg",
    "/Brand Activation/1208949e-1eaf-49a7-a167-7a1053f54feb.jpg",
    "/Brand Activation/1d77134c-40e7-4921-8def-fcd9de5f89e7.jpg",
    "/Brand Activation/1fe75e8f-e66b-41ee-abc7-a5cda3cc50e8.jpg",
    "/Brand Activation/1ffa8fca-4fab-4501-97c0-b08a622739cb.jpg",
    "/Brand Activation/22cd24cc-70b1-4848-ab6d-048f33401702.jpg",
    "/Brand Activation/23aa4e72-1804-4990-9330-ed5d972acaea.jpg",
    "/Brand Activation/278dc362-adef-4f21-a366-f6e161ec61ec.jpg",
    "/Brand Activation/2ecdab3e-2fc7-4e3c-bc8f-14a077ed92e2.jpg",
    "/Brand Activation/35c616dc-cd75-4b61-b3fa-dceb67c26a33.jpg",
    "/Brand Activation/4d115046-bb70-4649-acfd-63f313baccbb.jpg",
    "/Brand Activation/5cb88336-87b9-433e-862a-a657d7540dce.jpg",
    "/Brand Activation/628a55dd-069b-4712-9c2b-df3bc89cf818.jpg",
    "/Brand Activation/7ff64ce0-02cf-4165-bf28-f05d9d03eb0a.jpg",
    "/Brand Activation/8d72f809-ca56-4361-b189-a19da7dbd299.jpg",
    "/Brand Activation/9d39466e-acd8-4392-8840-bd4392cfd233.jpg",
    "/Brand Activation/a7a5c95e-4fb4-416e-a3d5-f84865b37b99.jpg",
    "/Brand Activation/af1c54dc-e463-48b4-97c6-b2e3ab20ea0e.jpg",
    "/Brand Activation/b8a21fbb-be2d-4ed8-901f-cefc08c1b9cb.jpg",
    "/Brand Activation/ba57ded0-2fa4-4ebb-9a3d-66d425089a09.jpg",
    "/Brand Activation/bac4f2e8-b1dd-417d-830d-179783e8ee9c.jpg",
    "/Brand Activation/c9a64e18-b583-4360-8914-9d9458fdda93.jpg",
    "/Brand Activation/c9a798a2-8d63-40b4-983f-ecc7d3e315b6.jpg",
    "/Brand Activation/d14bf54b-6514-440d-8e95-badb4edf33c5.jpg",
    "/Brand Activation/d1bba38e-09f9-47eb-aa0d-d3d83cc72796.jpg",
    "/Brand Activation/d37e8392-b40c-42f1-a2a6-83babe731eec.jpg",
    "/Brand Activation/d3c6cbdc-6910-426f-855f-870fd358f4d8.jpg",
    "/Brand Activation/d964083a-9ecf-4e84-a8c5-db1d56f72188.jpg",
    "/Brand Activation/ffe7af69-b65d-4737-962e-e8b14ed5a156.jpg",
    "/Brand Activation/IMG_2740.JPG",
    "/Brand Activation/IMG_2743.JPG",
    "/Brand Activation/ZPR_3819.jpeg",
    "/Brand Activation/ZPR_3819(1).jpeg",
    "/Brand Activation/ZPR_3823.jpeg",
    "/Brand Activation/ZPR_3824.jpeg",
    "/Brand Activation/ZPR_3825.jpeg",
    "/Brand Activation/ZPR_3829.jpeg",
    "/Brand Activation/ZPR_3836.jpeg",
    "/Brand Activation/ZPR_3837.jpeg",
    "/Brand Activation/ZPR_3848.jpeg"
  ];

  const stats = [
    { number: '42', label: 'Brand Activations', icon: '🎯' },
    { number: '12+', label: 'Industries', icon: '🏢' },
    { number: '100%', label: 'Engagement Rate', icon: '📈' },
    { number: '24/7', label: 'Support', icon: '🚀' }
  ];

  return (
    <div className="pt-32 min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-slate-900 via-orange-900 to-slate-900">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/10 via-red-600/10 to-yellow-600/10"></div>
          <div className="absolute top-20 left-20 w-32 h-32 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            ref={headerRef}
            className={`text-center transition-all duration-1000 ${
              headerVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-white text-sm font-medium mb-6">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <span>Experiential Marketing</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                Brand
              </span>
              <br />
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                Activation
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
              Explore our comprehensive brand activation campaigns, experiential marketing solutions, and interactive brand experiences that create lasting impressions and drive engagement.
            </p>

            {/* Stats */}
            <div 
              ref={statsRef}
              className={`grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto transition-all duration-1000 ${
                statsVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-10'
              }`}
            >
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-sm sm:text-base text-white/80">{stat.label}</div>
                  <div className="text-2xl mt-2">{stat.icon}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Brand Activation Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-gray-900 via-brand-primary to-gray-900 bg-clip-text text-transparent">
                Brand Activation Gallery
              </span>
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-brand-gold to-brand-orange mx-auto mb-4"></div>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              From interactive experiences to immersive campaigns, discover how our brand activations create memorable moments that connect brands with their audiences.
            </p>
          </div>

          {/* Brand Activation Images Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {brandActivationImages.map((image, index) => (
              <BrandActivationItem 
                key={index} 
                image={image} 
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-brand-primary to-brand-orange">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Activate Your Brand?
          </h3>
          <p className="text-xl text-white/90 mb-8">
            Let's create an unforgettable brand activation experience that engages your audience and drives results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/quote"
              className="bg-white text-brand-orange px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-lg hover:shadow-white/25 hover:scale-105 group"
            >
              <span className="flex items-center justify-center space-x-2">
                <span>Get Quote</span>
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </Link>
            <Link 
              to="/"
              className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-brand-orange transition-all duration-300 hover:shadow-lg hover:shadow-white/25 hover:scale-105 group"
            >
              <span className="flex items-center justify-center space-x-2">
                <span>Back to Home</span>
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BrandActivationPage;
